import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { CategoriaProductoApiService } from '../services/categoria-producto-api.service';
import { ProductCategoryResponse } from '../models/categoria-producto.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

@Component({
  selector: 'app-categoria-producto-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
  ],
  templateUrl: './categoria-producto-list.component.html',
})
export class CategoriaProductoListComponent implements OnInit {
  categorias: ProductCategoryResponse[] = [];
  loading = false;
  error = '';

  // Filtros
  showFilters = false;
  filtroNombre = '';
  filtroParent: number | undefined = undefined;
  filtroIsActive: boolean | undefined = undefined;

  //vista arbol
  expandedNodes = new Set<number>();

  gridCols =
    'minmax(220px,2fr) minmax(160px,1.5fr) minmax(120px,1fr) 100px 100px 110px';

  constructor(
    private categoriaApi: CategoriaProductoApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.cargarCategorias();
  }

  cargarCategorias(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.categoriaApi
      .searchProductCategory({ businessId, activeOnly: false })
      .subscribe({
        next: (data) => {
          this.categorias = data;
          this.expandAll();
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error al cargar categorías';
          this.loading = false;
          console.error('Error:', err);
        },
      });
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  limpiarFiltro(): void {
    this.filtroNombre = '';
    this.filtroParent = undefined;
    this.filtroIsActive = undefined;
  }

  nuevaCategoria(): void {
    this.router.navigate(['/categorias-producto/crear']);
  }

  editarCategoria(categoria: ProductCategoryResponse): void {
    this.router.navigate(['/categorias-producto/editar', categoria.id]);
  }

  async eliminarCategoria(categoria: ProductCategoryResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar categoría',
      message: `¿Está seguro de eliminar la categoría "${categoria.name}"?`,
    });
    if (!ok) return;
    this.categoriaApi.deleteProductCategory(categoria.id).subscribe({
      next: () => {
        this.cargarCategorias();
        this.toast.deleted('Categoría de producto');
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar la categoría',
        });
      },
    });
  }

  getParentLabel(categoria: ProductCategoryResponse): string {
    if (!categoria.idParent) return 'Raíz';
    if (categoria.parentName) return categoria.parentName;
    const parent = this.categorias.find((c) => c.id === categoria.idParent);
    return parent?.name || 'Raíz';
  }

  getTipoLabel(type: string): string {
    if (type === 'product') return 'Producto';
    if (type === 'service') return 'Servicio';
    return 'Ambos';
  }

  getTipoColor(type: string): BadgeColor {
    if (type === 'product') return 'primary';
    if (type === 'service') return 'warning';
    return 'info';
  }

  getActiveColor(categoria: ProductCategoryResponse): BadgeColor {
    return categoria.isActive ? 'success' : 'error';
  }

  getActiveLabel(categoria: ProductCategoryResponse): string {
    return categoria.isActive ? 'Activo' : 'Inactivo';
  }

  //vista arbol
  // Construye árbol desde array plano
  get categoriasTree(): ProductCategoryResponse[] {
    if (this.filtroParent !== undefined && this.filtroParent !== 0) {
      return this.categoriasFiltered.filter((c) => c.id === this.filtroParent);
    }

    if (this.filtroIsActive !== undefined) {
      return this.categoriasFiltered;
    }

    return this.categoriasFiltered.filter((c) => c.idParent === null);
  }

  get categoriasPadre(): ProductCategoryResponse[] {
    return this.categorias.filter((cat) =>
      this.categorias.some((hijo) => hijo.idParent === cat.id),
    );
  }

  toggleNode(id: number): void {
    if (this.expandedNodes.has(id)) {
      this.expandedNodes.delete(id);
    } else {
      this.expandedNodes.add(id);
    }
  }

  isExpanded(id: number): boolean {
    return this.expandedNodes.has(id);
  }

  tieneHijos(id: number): boolean {
    if (this.filtroIsActive !== undefined) {
      return false;
    }

    return this.categorias.some((c) => c.idParent === id);
  }

  expandAll(): void {
    this.categoriasFiltered.forEach((c) => this.expandedNodes.add(c.id));
  }

  collapseAll(): void {
    this.expandedNodes.clear();
  }

  //New Filters
  get categoriasFiltered(): ProductCategoryResponse[] {
    let result = this.categorias;

    result = this.filterByParent(result);
    result = this.filterByName(result);
    result = this.filterByStatus(result);

    return result;
  }

  private filterByName(
    categorias: ProductCategoryResponse[],
  ): ProductCategoryResponse[] {
    if (!this.filtroNombre) {
      return categorias;
    }

    const term = this.filtroNombre.toLowerCase();

    const resultado = new Set<number>();

    this.categorias.forEach((cat) => {
      if (cat.name.toLowerCase().includes(term)) {
        resultado.add(cat.id);

        this.getParents(cat).forEach((p) => resultado.add(p.id));

        this.getDirectChildren(cat.id).forEach((h) => resultado.add(h.id));
      }
    });

    return categorias.filter((cat) => resultado.has(cat.id));
  }

  private filterByStatus(
    categorias: ProductCategoryResponse[],
  ): ProductCategoryResponse[] {
    if (this.filtroIsActive === undefined || this.filtroIsActive === null) {
      return categorias;
    }

    return categorias.filter((cat) => cat.isActive === this.filtroIsActive);
  }

  private filterByParent(
    categorias: ProductCategoryResponse[],
  ): ProductCategoryResponse[] {
    if (this.filtroParent === undefined) {
      return categorias;
    }

    if (this.filtroParent === 0) {
      return categorias.filter(
        (cat) =>
          cat.idParent === null &&
          !categorias.some((hijo) => hijo.idParent === cat.id),
      );
    }

    const padre = categorias.find((cat) => cat.id === this.filtroParent);

    if (!padre) {
      return categorias;
    }

    const hijos = categorias.filter((cat) => cat.idParent === padre.id);

    return [padre, ...hijos];
  }

  getDirectChildren(id: number): ProductCategoryResponse[] {
    return this.categorias.filter((cat) => cat.idParent === id);
  }

  private getParents(cat: ProductCategoryResponse): ProductCategoryResponse[] {
    const padres: ProductCategoryResponse[] = [];

    let actual = cat;

    while (actual.idParent !== null) {
      const padre = this.categorias.find((c) => c.id === actual.idParent);

      if (!padre) {
        break;
      }

      padres.unshift(padre);
      actual = padre;
    }

    return padres;
  }
}
